(() => {
  if (window.__zingRelatedProductsInit) return;
  window.__zingRelatedProductsInit = true;

  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  const RELATED_SELECTOR = '[data-related-products-section]';
  const CACHE = new Map();
  const MAX_PRODUCTS = 3;

  const normalise = (value) => String(value || '').trim().toLowerCase();
  const tagsOf = (product) => {
    const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
    return tags.map(normalise).filter(Boolean);
  };
  const hasText = (product, needle) => {
    const n = normalise(needle);
    const haystack = [product?.title, product?.vendor, product?.type, ...tagsOf(product)].map(normalise).join(' | ');
    return haystack.includes(n);
  };
  const productHandleFromLink = (link) => {
    if (!link) return '';
    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };
  const currentHandle = () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };
  const fetchProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!CACHE.has(handle)) {
      CACHE.set(handle, fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
        .catch(() => null));
    }
    return CACHE.get(handle);
  };

  const priceOf = (product) => Number(product?.price_min ?? product?.price ?? 0) || 0;

  const familySignals = [
    'orchard collection',
    'splash gummies',
    'bubs',
    'pez',
    'efrutti',
    'bonbon',
    'peelerz'
  ];
  const sensorySignals = [
    'sour', 'foam', 'foamy', 'licorice', 'liquorice', 'soft', 'chewy',
    'gummy', 'gummies', 'liquid', 'filled', 'peel', 'peelable', 'fruity',
    'cola', 'berry', 'raspberry', 'strawberry', 'banana', 'toffee', 'pear',
    'pineapple', 'peach', 'watermelon', 'lemon', 'vanilla', 'tutti frutti'
  ];

  const scoreCandidate = (current, candidate, nativeIndex) => {
    let score = Math.max(0, 12 - nativeIndex);
    const currentVendor = normalise(current?.vendor);
    const candidateVendor = normalise(candidate?.vendor);
    const currentType = normalise(current?.type);
    const candidateType = normalise(candidate?.type);

    if (currentVendor && candidateVendor && currentVendor === candidateVendor && currentVendor !== 'zingcandy') score += 38;
    if (currentType && candidateType && currentType === candidateType) score += 20;

    familySignals.forEach((signal) => {
      if (hasText(current, signal) && hasText(candidate, signal)) score += signal === 'bubs' ? 48 : 42;
    });

    sensorySignals.forEach((signal) => {
      if (hasText(current, signal) && hasText(candidate, signal)) score += 9;
    });

    if (hasText(current, 'swedish candy') && hasText(candidate, 'swedish candy')) score += 12;
    if ((currentType === 'swedish candy' || currentType === 'candy mix') && candidateType === 'candy gift box' && hasText(candidate, 'swedish')) score += 9;

    const currentPrice = priceOf(current);
    const candidatePrice = priceOf(candidate);
    if (currentPrice > 0 && candidatePrice > currentPrice && candidatePrice <= currentPrice * 1.8) score += 7;

    return score;
  };

  const directProductCards = (grid) => Array.from(grid?.children || []).filter((node) => node.querySelector?.(PRODUCT_LINK_SELECTOR));

  const contextualise = async () => {
    const related = document.querySelector(RELATED_SELECTOR);
    if (!related || related.dataset.zingContextualized === 'true' || related.dataset.zingContextualizing === 'true') return false;

    const grid = related.querySelector('ul');
    const existingCards = directProductCards(grid);
    if (!grid || existingCards.length === 0) return false;

    related.dataset.zingContextualizing = 'true';
    try {
      const handle = currentHandle();
      const current = await fetchProduct(handle);
      if (!current?.id) return false;

      const sectionRoot = related.closest('[id^="shopify-section-"]');
      const sectionId = sectionRoot?.id?.replace(/^shopify-section-/, '');
      if (!sectionId) return false;

      const params = new URLSearchParams({
        section_id: sectionId,
        product_id: String(current.id),
        limit: '10',
        intent: 'related'
      });
      const response = await fetch(`/recommendations/products?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) return false;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sourceGrid = doc.querySelector(`${RELATED_SELECTOR} ul`) || doc.querySelector('ul');
      const sourceCards = directProductCards(sourceGrid);
      if (sourceCards.length === 0) return false;

      const candidates = [];
      const seen = new Set();
      sourceCards.forEach((card, index) => {
        const candidateHandle = productHandleFromLink(card.querySelector(PRODUCT_LINK_SELECTOR));
        if (!candidateHandle || candidateHandle === handle || seen.has(candidateHandle)) return;
        seen.add(candidateHandle);
        candidates.push({ card, handle: candidateHandle, nativeIndex: index });
      });
      if (candidates.length === 0) return false;

      const products = await Promise.all(candidates.map((item) => fetchProduct(item.handle)));
      const ranked = candidates
        .map((item, index) => ({ ...item, product: products[index] }))
        .filter((item) => item.product && item.product.available !== false)
        .map((item) => ({ ...item, score: scoreCandidate(current, item.product, item.nativeIndex) }))
        .sort((a, b) => b.score - a.score || a.nativeIndex - b.nativeIndex);

      const desiredCount = Math.min(existingCards.length, MAX_PRODUCTS);
      const selected = ranked.slice(0, desiredCount);
      if (selected.length === 0) return false;

      const insertBefore = Array.from(grid.children).find((node) => !node.querySelector?.(PRODUCT_LINK_SELECTOR)) || null;
      existingCards.forEach((card) => card.remove());
      selected.forEach((item) => {
        const node = document.importNode(item.card, true);
        grid.insertBefore(node, insertBefore);
      });

      directProductCards(grid).slice(MAX_PRODUCTS).forEach((card) => card.remove());
      related.dataset.zingContextualized = 'true';
      return true;
    } catch (_) {
      return false;
    } finally {
      delete related.dataset.zingContextualizing;
    }
  };

  const start = () => {
    let attempts = 0;
    const tryRun = () => {
      attempts += 1;
      contextualise().then((done) => {
        if (!done && attempts < 8) window.setTimeout(tryRun, attempts * 180);
      });
    };
    tryRun();

    const observer = new MutationObserver(() => {
      const related = document.querySelector(RELATED_SELECTOR);
      if (related?.dataset.zingContextualized === 'true') {
        observer.disconnect();
        return;
      }
      contextualise().then((done) => { if (done) observer.disconnect(); });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 8000);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();